
import React, { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { getActiveDOMDocument } from '../../../base/config/functions.any';
import { isLocalParticipantModerator } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { getVerticalFilmstripVisibleAreaWidth } from '../../../filmstrip/functions.web';
import { getCurrentLayout } from '../../../video-layout';
import { getGenericIFrameUrl } from '../../functions';

/**
 * Default genericiframe frame background.
 */
const DEFAULT_BACKGROUND = 'white';

/**
 * The type of the React {@code Component} props of {@link GenericIFrame}.
 */
type Props = {

    /**
     * The Redux dispatch function.
     */
    dispatch: Dispatch<any>,

    /**
     * URL for the generic iframe.
     */
    _documentUrl: string,

    /**
     * Whether the iframe is visible.
     */
    _isVisible: boolean,

    /**
     * Whether the local participant is a moderator.
     */
    _isModerator: boolean,

    /**
     * The current layout mode.
     */
    _currentLayout: string,

    /**
     * The filmstrip width (vertical mode).
     */
    _verticalFilmstripWidth: number,

    /**
     * The filmstrip height (horizontal mode).
     */
    _filmstripHeight: number,
};

/**
 * Implements a React component for the generic iframe container.
 *
 * @augments Component
 */
class GenericIFrame extends PureComponent<Props> {
    _iframeRef: Object;
    _containerRef: Object;
    _bodyBackground: string;
    _protectionInterval: ?IntervalID;
    _eventListeners: Array<{element: HTMLElement, event: string, handler: Function}>;

    /**
     * Initializes a new {@code GenericIFrame} instance.
     *
     * @param {Props} props - The React {@code Component} props.
     */
    constructor(props: Props) {
        super(props);

        this._iframeRef = React.createRef();
        this._containerRef = React.createRef();
        this._bodyBackground = '';
        this._protectionInterval = null;
        this._eventListeners = [];
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        window.addEventListener('resize', this._handleResize);
        this._handleResize();
        this._applyProtection();
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (this.props._isVisible !== prevProps._isVisible) {
            if (this.props._isVisible) {
                this._show();
            } else {
                this._hide();
            }
        }

        if (this.props._isModerator !== prevProps._isModerator) {
            this._applyProtection();
        }

        this._handleResize();
    }

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        window.removeEventListener('resize', this._handleResize);
        this._restoreBodyBackground();
        this._removeProtection();
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const { _documentUrl, _isVisible, _isModerator } = this.props;

        const containerStyle = {
            zIndex: _isVisible ? '10' : '0',
            position: 'absolute',
            pointerEvents: _isVisible && !_isModerator ? 'none' : 'auto'
        };

        const iframeStyle = {
            width: '100%',
            height: '100%',
            background: DEFAULT_BACKGROUND,
            visibility: _isVisible ? 'visible' : 'hidden',
            display: _isVisible ? 'block' : 'none',
            opacity: _isVisible ? '1' : '0',
            transition: 'opacity 300ms',
            border: '0',
            borderRadius: '25px',
            position: 'absolute',
            top: 0,
            left: 0
        };

        return (
            <div
                id = 'genericiframe'
                ref = { this._containerRef }
                style = { containerStyle }>
                <iframe
                    frameBorder = '0'
                    id = 'genericiframe-iframe'
                    ref = { this._iframeRef }
                    scrolling = 'no'
                    src = { _documentUrl }
                    style = { iframeStyle }
                    title = 'Generic IFrame' />
            </div>
        );
    }

    _applyProtection: () => void;

    /**
     * Applies DOM protection for non-moderators.
     *
     * @private
     * @returns {void}
     */
    _applyProtection = () => {
        const { _isModerator, _isVisible } = this.props;

        // Remove existing protection first
        this._removeProtection();

        if (!_isModerator && _isVisible) {
            // Force pointer events via JavaScript
            this._enforcePointerEvents();

            // Block all pointer events
            this._blockPointerEvents();

            // Enable wheel/scroll but prevent other interactions
            this._setupWheelEvents();

            // Lock keyboard events
            this._lockKeyboard();

            // Set up continuous protection
            this._protectionInterval = setInterval(() => {
                this._enforcePointerEvents();
            }, 100);
        }
    };

    _removeProtection: () => void;

    /**
     * Removes all protection mechanisms.
     *
     * @private
     * @returns {void}
     */
    _removeProtection = () => {
        if (this._protectionInterval) {
            clearInterval(this._protectionInterval);
            this._protectionInterval = null;
        }

        // Remove all event listeners
        // The capture flag must match the one used when adding, or the removal silently does nothing.
        this._eventListeners.forEach(({ element, event, handler, capture = true }) => {
            element.removeEventListener(event, handler, capture);
        });
        this._eventListeners = [];
    };

    _enforcePointerEvents: () => void;

    /**
     * Continuously enforces pointer-events: none on the container.
     *
     * @private
     * @returns {void}
     */
    _enforcePointerEvents = () => {
        if (this._containerRef.current && this._iframeRef.current) {
            const container = this._containerRef.current;
            const iframe = this._iframeRef.current;

            // Force pointer-events via JavaScript (harder to override via devtools)
            container.style.setProperty('pointer-events', 'none', 'important');
            iframe.style.setProperty('pointer-events', 'none', 'important');

            // Check if pointer-events is being overridden
            const containerComputed = window.getComputedStyle(container).pointerEvents;
            const iframeComputed = window.getComputedStyle(iframe).pointerEvents;

            if (containerComputed !== 'none') {
                container.style.cssText = container.style.cssText.replace(/pointer-events:\s*\w+\s*!important;?/gi, '');
                container.style.setProperty('pointer-events', 'none', 'important');
            }

            if (iframeComputed !== 'none') {
                iframe.style.cssText = iframe.style.cssText.replace(/pointer-events:\s*\w+\s*!important;?/gi, '');
                iframe.style.setProperty('pointer-events', 'none', 'important');
            }
        }
    };

    _blockPointerEvents: () => void;

    /**
     * Blocks all pointer events on the iframe container.
     *
     * @private
     * @returns {void}
     */
    _blockPointerEvents = () => {
        if (!this._containerRef.current) {
            return;
        }

        const container = this._containerRef.current;
        const pointerEvents = [
            'click', 'dblclick', 'mousedown', 'mouseup', 'mousemove',
            'touchstart', 'touchend', 'touchmove', 'touchcancel',
            'pointerdown', 'pointerup', 'pointermove', 'pointercancel',
            'contextmenu', 'selectstart', 'dragstart'
        ];

        const blockEvent = e => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            return false;
        };

        pointerEvents.forEach(eventType => {
            container.addEventListener(eventType, blockEvent, true);
            this._eventListeners.push({
                element: container,
                event: eventType,
                handler: blockEvent
            });
        });
    };

    _setupWheelEvents: () => void;

    /**
     * Sets up wheel events to allow scrolling.
     *
     * @private
     * @returns {void}
     */
    _setupWheelEvents = () => {
        if (!this._containerRef.current) {
            return;
        }

        const container = this._containerRef.current;

        const handleWheel = e => {
            // Allow wheel event to propagate (for scrolling)
            // But prevent any other interactions
            e.stopPropagation();
        };

        container.addEventListener('wheel', handleWheel, { passive: true });
        this._eventListeners.push({
            capture: false,
            element: container,
            event: 'wheel',
            handler: handleWheel
        });
    };

    _lockKeyboard: () => void;

    /**
     * Locks keyboard events to prevent manipulation.
     *
     * @private
     * @returns {void}
     */
    _lockKeyboard = () => {
        if (!this._containerRef.current) {
            return;
        }

        const container = this._containerRef.current;
        const keyboardEvents = [ 'keydown', 'keyup', 'keypress' ];

        const blockKeyboard = e => {
            // Block F12, Ctrl+Shift+I, Ctrl+Shift+C (devtools shortcuts)
            if (
                e.key === 'F12'
                || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J'))
                || (e.metaKey && e.altKey && (e.key === 'I' || e.key === 'C' || e.key === 'J'))
            ) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                return false;
            }

            // Block other keyboard interactions with the iframe
            if (e.target === container || container.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();

                return false;
            }
        };

        keyboardEvents.forEach(eventType => {
            document.addEventListener(eventType, blockKeyboard, true);
            this._eventListeners.push({
                element: document,
                event: eventType,
                handler: blockKeyboard
            });
        });
    };

    _handleResize: () => void;

    /**
     * Handles window resize events.
     *
     * @private
     * @returns {void}
     */
    _handleResize = () => {
        if (!this._iframeRef.current || !this.props._isVisible) {
            return;
        }

        const state = APP.store.getState();
        const activeWindow = getActiveDOMDocument(state);
        const container = activeWindow.getElementById('largeVideoContainer');

        if (!container) {
            return;
        }

        this._iframeRef.current.style.width = '100%';
        this._iframeRef.current.style.height = '100%';
    };

    _show: () => void;

    /**
     * Shows the iframe with fade-in effect.
     *
     * @private
     * @returns {void}
     */
    _show = () => {
        if (!this._iframeRef.current) {
            return;
        }

        // Save current body background
        this._bodyBackground = document.body.style.background;

        // Apply fade-in effect
        setTimeout(() => {
            document.body.style.background = '#eeeeee';
        }, 300);

        this._handleResize();
        this._applyProtection();
    };

    _hide: () => void;

    /**
     * Hides the iframe with fade-out effect.
     *
     * @private
     * @returns {void}
     */
    _hide = () => {
        this._restoreBodyBackground();
        this._removeProtection();

        const dominantSpeaker = document.getElementById('dominantSpeaker');

        if (dominantSpeaker) {
            dominantSpeaker.style.visibility = 'visible';
        }
    };

    _restoreBodyBackground: () => void;

    /**
     * Restores the original body background.
     *
     * @private
     * @returns {void}
     */
    _restoreBodyBackground = () => {
        if (this._bodyBackground) {
            document.body.style.background = this._bodyBackground;
        }
    };
}

/**
 * Maps (parts of) the redux state to {@link GenericIFrame} React {@code Component} props.
 *
 * @param {Object} state - The redux store/state.
 * @private
 * @returns {Object}
 */
function _mapStateToProps(state: Object) {
    const { visible } = state['features/genericiframe'];
    const documentUrl = getGenericIFrameUrl(state);
    const currentLayout = getCurrentLayout(state);

    return {
        _documentUrl: documentUrl,
        _isVisible: visible,
        _isModerator: isLocalParticipantModerator(state),
        _currentLayout: currentLayout,
        _verticalFilmstripWidth: getVerticalFilmstripVisibleAreaWidth(),
        _filmstripHeight: state['features/filmstrip'].horizontalViewDimensions?.filmstripHeight || 0
    };
}

export default connect(_mapStateToProps)(GenericIFrame);
